import { CATALOG_V1_ART_SHOWROOM_RIGHTS } from './art-showroom-rights';
import { CATALOG_V1_GAMEPLAY_MEDIA } from './gameplayShowroomMedia';
export { CATALOG_V1_PUBLIC_GALLERY } from './public-gallery';
function deepFreeze(value) {
    if (value === null || typeof value !== 'object' || Object.isFrozen(value))
        return value;
    const objectValue = value;
    for (const property of Reflect.ownKeys(objectValue)) {
        deepFreeze(Reflect.get(objectValue, property));
    }
    return Object.freeze(value);
}
// Keep the published list's fallback priority independent of detail-only views.
export const CATALOG_V1_LIST_MEDIA_FILES = deepFreeze([
    'packshot-eshop-v2.png',
    'preview-poster-easel.png',
    'preview-poster-tabletop-easel.png',
    'preview-poster-easel-front-label.png',
    'preview-poster-easel-posed.png',
    'preview-poster-living-room-wall.png',
]);
export const CATALOG_V1_SHOWROOM_DECO_FILES = deepFreeze([
    ...CATALOG_V1_LIST_MEDIA_FILES,
    'preview-puzzle-poster.png',
    'preview-poster-play-family.png',
]);
export const CATALOG_V1_OFFICIAL_CREDITS = deepFreeze({
    birdie: '© BIRDIE',
    lePetitPrince: 'Le Petit Prince ® © SOGEX 2023',
    leMurDesJeTaime: 'LE MUR DES JE T’AIME© ADAGP 2024',
    samyHalim: '© SAMY HALIM',
    unitedSquare: '© UNITED SQUARE',
    imagiciel: '© IMAGICIEL',
});
const DENIED_COMMERCIAL_AUTHORIZATION = {
    commercialActivationAuthorized: false,
    shopifyPublicationAuthorized: false,
    checkoutAuthorized: false,
};
const STUDIO_PREVIEW_TRANSFORMATION = 'Studio preserves the stored poster master as the print source, renders poster-source.png as its transformed raster proxy, and composes the managed DECO previews from that proxy.';
const PUBLIC_DOMAIN_PRODUCTION_MANAGEMENT = 'Production artwork managed by IMAGICIEL, derived from a public-domain underlying work.';
function unpinnedPublicPreviewManifest() {
    return CATALOG_V1_LIST_MEDIA_FILES.map((assetPath) => ({
        assetPath,
        sourceAssetPath: 'poster-source.png',
        sourceSha256: null,
        fingerprint: null,
    }));
}
function confirmedMedia(position, themeId, studioSourceFileName, storedPosterMasterPath, credit, rightsEvidence, evidence = {}) {
    return {
        position,
        themeId,
        credit: typeof credit === 'string' ? { fr: credit, en: credit } : credit,
        underlyingWorkReference: evidence.underlyingWorkReference ?? null,
        productionAssetProvenance: {
            studioSourceFileName,
            storedPosterMasterPath,
            previewSourceAssetPath: 'poster-source.png',
            masterAssetFingerprint: evidence.masterAssetFingerprint ?? null,
            previewSourceAssetFingerprint: evidence.previewSourceAssetFingerprint ?? null,
            managementStatement: evidence.managementStatement ?? null,
            transformationStatement: STUDIO_PREVIEW_TRANSFORMATION,
        },
        publicPreviewManifest: [
            ...(evidence.publicPreviewManifest ?? unpinnedPublicPreviewManifest()),
            ...(CATALOG_V1_GAMEPLAY_MEDIA[themeId] ?? []),
        ],
        rightsEvidence,
        authorizedPublicFiles: CATALOG_V1_SHOWROOM_DECO_FILES,
        authorization: {
            showroomPublicationAuthorized: true,
            ...DENIED_COMMERCIAL_AUTHORIZATION,
        },
        readinessBlockers: [],
    };
}
function confirmedArtMedia(position, themeId) {
    const rights = CATALOG_V1_ART_SHOWROOM_RIGHTS[themeId];
    const previewSourceFingerprint = rights.productionAsset.previewSource;
    return confirmedMedia(position, themeId, rights.studioSourceFileName, rights.productionAsset.master.path, rights.credit, 'public-domain-underlying-work-and-pinned-production', {
        underlyingWorkReference: rights.underlyingWorkReference,
        masterAssetFingerprint: {
            byteSize: rights.productionAsset.master.byteSize,
            sha256: rights.productionAsset.master.sha256,
        },
        previewSourceAssetFingerprint: {
            byteSize: previewSourceFingerprint.byteSize,
            sha256: previewSourceFingerprint.sha256,
        },
        managementStatement: PUBLIC_DOMAIN_PRODUCTION_MANAGEMENT,
        publicPreviewManifest: rights.publicPreviewManifest.map((preview) => ({
            assetPath: preview.assetPath,
            sourceAssetPath: previewSourceFingerprint.path,
            sourceSha256: previewSourceFingerprint.sha256,
            fingerprint: { byteSize: preview.byteSize, sha256: preview.sha256 },
        })),
    });
}
export const CATALOG_V1_SHOWROOM_MEDIA = deepFreeze([
    confirmedMedia(1, 'united-square-original', 'POSTER UNITED SQUARE ORIGINAL.jpg', 'poster-original.jpg', CATALOG_V1_OFFICIAL_CREDITS.unitedSquare, 'studio-permanent-copyright'),
    confirmedArtMedia(2, 'art-01-la-vague'),
    confirmedArtMedia(3, 'art-02-le-baiser'),
    confirmedArtMedia(4, 'art-03-la-nuit-etoilee'),
    confirmedMedia(5, 'birdie-01-bistrot', 'POSTER_BIRDIE_BISTROT.pdf', 'poster-original.pdf', CATALOG_V1_OFFICIAL_CREDITS.birdie, 'studio-approved-license'),
    confirmedMedia(6, 'birdie-02-boulangerie', 'POSTER_BIRDIE_BOULANGERIE.pdf', 'poster-original.pdf', CATALOG_V1_OFFICIAL_CREDITS.birdie, 'studio-approved-license'),
    confirmedMedia(7, 'birdie-03-fromagerie', 'POSTER_BIRDIE_FROMAGERIE.pdf', 'poster-original.pdf', CATALOG_V1_OFFICIAL_CREDITS.birdie, 'studio-approved-license'),
    confirmedMedia(8, 'birdie-04-les-toits', 'POSTER_BIRDIE_LES TOITS.pdf', 'poster-original.pdf', CATALOG_V1_OFFICIAL_CREDITS.birdie, 'studio-approved-license'),
    confirmedMedia(9, 'birdie-05-patisserie', 'POSTER_BIRDIE_PATISSERIE.pdf', 'poster-original.pdf', CATALOG_V1_OFFICIAL_CREDITS.birdie, 'studio-approved-license'),
    confirmedMedia(10, 'le-petit-prince-01-la-rose', 'POSTER_LE PETIT PRINCE_No .01_LA ROSE.pdf', 'poster-original.pdf', CATALOG_V1_OFFICIAL_CREDITS.lePetitPrince, 'studio-approved-license'),
    confirmedMedia(11, 'le-petit-prince-02-la-boite', 'POSTER_LE PETIT PRINCE_No .02_LA BOITE.pdf', 'poster-original.pdf', CATALOG_V1_OFFICIAL_CREDITS.lePetitPrince, 'studio-approved-license'),
    confirmedMedia(12, 'le-petit-prince-03-best-friends', 'POSTER_LE PETIT PRINCE_No .03_BEST FRIENDS.pdf', 'poster-original.pdf', CATALOG_V1_OFFICIAL_CREDITS.lePetitPrince, 'studio-approved-license'),
    confirmedMedia(13, 'le-petit-prince-04-look-with-the-heart', 'POSTER_LE PETIT PRINCE_No .04_LOOK WITH THE HEART.pdf', 'poster-original.pdf', CATALOG_V1_OFFICIAL_CREDITS.lePetitPrince, 'studio-approved-license'),
    confirmedMedia(14, 'le-petit-prince-05-vois-avec-le-coeur', 'POSTER_LE PETIT PRINCE_No .05_VOIS AVEC LE COEUR.pdf', 'poster-original.pdf', CATALOG_V1_OFFICIAL_CREDITS.lePetitPrince, 'studio-approved-license'),
    confirmedMedia(15, 'samy-halim-01-keli', 'POSTER_SAMY HALIM_KELI.pdf', 'poster-original.pdf', CATALOG_V1_OFFICIAL_CREDITS.samyHalim, 'studio-approved-license'),
    confirmedMedia(16, 'samy-halim-02-kenza', 'POSTER_SAMY HALIM_KENZA.pdf', 'poster-original.pdf', CATALOG_V1_OFFICIAL_CREDITS.samyHalim, 'studio-approved-license'),
    confirmedMedia(17, 'samy-halim-03-victoria', 'POSTER_SAMY HALIM_VICTORIA.pdf', 'poster-original.pdf', CATALOG_V1_OFFICIAL_CREDITS.samyHalim, 'studio-approved-license'),
    confirmedMedia(18, 'crazy-new-york-01', 'POSTER_CRAZY NEW YORK.pdf', 'poster-original.pdf', CATALOG_V1_OFFICIAL_CREDITS.imagiciel, 'julien-showroom-decision'),
    confirmedMedia(19, 'pop-art-01', 'POSTER_POP ART.pdf', 'poster-original.pdf', CATALOG_V1_OFFICIAL_CREDITS.imagiciel, 'julien-showroom-decision'),
    confirmedMedia(20, 'le-mur-des-je-t-aime-01', 'POSTER_LE MUR DES JE TAIME.pdf', 'poster-original.pdf', CATALOG_V1_OFFICIAL_CREDITS.leMurDesJeTaime, 'studio-approved-license'),
]);
export * from './prepressJobs';
export * from './integrationJobs';
